import React, { useCallback, useEffect, useState } from 'react';
import CommonLoader from '../../../Components/CommonLoader';
import NoDataPage from '../../../Components/NoDataPage';
import CommonModal from '../../../Components/CommonModal';
import CommonTable from '../../../Components/CommonTable';
import StatCard from '../../../Components/StatCard';
import StatusBadge from '../../../Components/StatusBadge';
import { useToast } from '../../../Components/CommonToast';
import { Heading, Body } from '../../../Constants/Fonts';
import { risk_portal_compliance_coverage } from '../../../services';

// Unwrap the AIGApi / SbomApiIntercepter envelope — same helper the sibling
// pages use.
const unwrap = (res) =>
    res?.data?.response_data?.data ?? res?.data?.data ??
    res?.statusCode?.response_data?.data ?? res?.statusCode?.data ?? null;

// Coverage % → semantic colour: strong ≥ 80 (success), partial ≥ 50 (amber),
// weak / not started otherwise (danger).
const coverageColor = (pct) =>
    pct >= 80 ? 'var(--color-success)' : pct >= 50 ? 'var(--color-medium)' : 'var(--color-danger)';
const coverageLabel = (pct) =>
    pct === 0 ? 'Not started' : pct >= 80 ? 'On track' : 'In progress';

const titleCase = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Coverage cell — status label + %, with a thin progress bar beneath.
function CoverageCell({ pct }) {
    const color = coverageColor(pct);
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 200 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <Body as="span" variant={2} weight="semibold" color={color}>{coverageLabel(pct)}</Body>
                <Body as="span" variant={2} weight="semibold" color="var(--color-text-secondary)">{pct}%</Body>
            </div>
            <div style={{ height: 6, borderRadius: 999, background: 'var(--color-surface-muted)', overflow: 'hidden' }}>
                <div style={{ width: `${Math.max(0, Math.min(100, pct))}%`, height: '100%', background: color, borderRadius: 999, transition: 'width .3s ease' }} />
            </div>
        </div>
    );
}

export default function ComplianceCoverage() {
    const [frameworks, setFrameworks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [gapsFor, setGapsFor] = useState(null); // framework whose gaps modal is open
    const { notify, toastNode } = useToast();

    const load = useCallback(async () => {
        try {
            const d = unwrap(await risk_portal_compliance_coverage());
            setFrameworks(Array.isArray(d) ? d : (Array.isArray(d?.frameworks) ? d.frameworks : (Array.isArray(d?.coverage) ? d.coverage : [])));
        } catch (e) {
            notify('Failed to load compliance coverage', 'error');
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => { setLoading(true); load(); }, [load]);

    if (loading) return <CommonLoader fullPage label="Loading compliance coverage…" />;

    // Normalise each framework's derived numbers once.
    const rowsData = frameworks.map((fw) => {
        const total = fw.total_controls ?? 0;
        const mapped = fw.mapped_controls ?? 0;
        const gaps = fw.gaps ?? (total - mapped);
        const pct = Math.round(fw.coverage_pct ?? (total > 0 ? (mapped / total) * 100 : 0));
        const unmapped = Array.isArray(fw.unmapped_controls) ? fw.unmapped_controls : [];
        return { fw, total, mapped, gaps, pct, unmapped };
    });

    // Portfolio summary across all frameworks.
    const totalControls = rowsData.reduce((s, r) => s + r.total, 0);
    const mappedControls = rowsData.reduce((s, r) => s + r.mapped, 0);
    const openGaps = rowsData.reduce((s, r) => s + r.gaps, 0);
    const avgCoverage = rowsData.length
        ? Math.round(rowsData.reduce((s, r) => s + r.pct, 0) / rowsData.length)
        : 0;

    const tableRows = rowsData.map(({ fw, total, mapped, gaps, pct, unmapped }) => ({
        data: [
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                <Body as="span" weight="semibold" color="var(--color-text-primary)">{fw.framework_name}</Body>
                <StatusBadge variant={fw.framework_type === 'custom' ? 'neutral' : 'info'} dot={false} label={titleCase(fw.framework_type)} />
            </span>,
            <CoverageCell pct={pct} />,
            <Body as="span" weight="bold" color="var(--color-text-primary)">{total}</Body>,
            <Body as="span" weight="bold" color="var(--color-success)">{mapped}</Body>,
            <Body as="span" weight="bold" color={gaps > 0 ? 'var(--color-danger)' : 'var(--color-success)'}>{gaps}</Body>,
            gaps > 0 && unmapped.length > 0
                ? <button type="button" className="rp1_link_btn" onClick={() => setGapsFor({ ...fw, unmapped })}>View {gaps} gaps →</button>
                : <Body as="span" variant={2} color="var(--color-text-muted)">—</Body>,
        ],
    }));

    const gapRows = (Array.isArray(gapsFor?.unmapped) ? gapsFor.unmapped : []).map((c, idx) => ({
        data: [
            <Body as="span" variant={2} weight="semibold" color="var(--color-text-primary)">{c.control_ref || `#${idx + 1}`}</Body>,
            c.title || '—',
        ],
    }));

    return (
        <div className="rp1_widget">
            <div style={{ marginBottom: 16 }}>
                <Heading variant={3} style={{ margin: 0 }}>Compliance Coverage</Heading>
                <Body variant={2} color="var(--color-text-muted)" style={{ margin: '4px 0 0', maxWidth: 760 }}>
                    Shows how many framework controls are addressed by risks in your registry. Unmapped controls represent compliance gaps.
                </Body>
            </div>

            {frameworks.length === 0 ? (
                <NoDataPage
                    title="No frameworks configured"
                    hint="Set up audit frameworks in the Internal Audit module, then map risks to their controls."
                />
            ) : (
                <>
                    {/* Portfolio summary */}
                    <div className="rp1_stat_row" style={{ marginBottom: 20 }}>
                        <StatCard className="cmn_elevated_card" label="Frameworks tracked" value={rowsData.length} />
                        <StatCard className="cmn_elevated_card" label="Total controls" value={totalControls} />
                        <StatCard className="cmn_elevated_card" label="Mapped controls" value={mappedControls} valueColor="var(--color-success)" />
                        <StatCard className="cmn_elevated_card" label="Open gaps" value={openGaps} valueColor={openGaps > 0 ? 'var(--color-danger)' : 'var(--color-success)'} />
                        <StatCard className="cmn_elevated_card" label="Average coverage" value={`${avgCoverage}%`} />
                    </div>

                    {/* Per-framework coverage table */}
                    <CommonTable
                        headers={['Framework', 'Coverage', 'Total', 'Mapped', 'Gaps', '']}
                        rows={tableRows}
                        noPadding
                        className="cmn_elevated_card"
                    />
                </>
            )}

            {/* Unmapped controls (gaps) — shown in a shared CommonTable modal */}
            <CommonModal
                show={!!gapsFor}
                onHide={() => setGapsFor(null)}
                mid
                title={gapsFor ? `${gapsFor.framework_name} — Unmapped Controls` : ''}
                subtitle="These controls have no risk mapped to them and represent compliance gaps."
            >
                <CommonTable headers={['Control', 'Title']} rows={gapRows} noPadding noScroll />
            </CommonModal>

            {toastNode}
        </div>
    );
}
